import React, { useState } from 'react';
import { Button, Form, Spinner, ModalFooter } from 'react-bootstrap';
import Toaster from 'src/utils/toaster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { ISolutionRoleDTO } from 'src/data-access/models/response/solution-user';
import { GenerateSolutionRequestsAPI } from 'src/data-access/api-services';
import { SolutionStatus, SolutionUserStatus } from 'src/data-access/models/enums';
import InfoTooltip from './info-tooltip';

interface SolutionRequestProps {
    solutionAccessData: ISolutionRoleDTO[];
    onSolutionRequestSent: () => void;
}

const SolutionRequestGenerator: React.FC<SolutionRequestProps> = ({ solutionAccessData, onSolutionRequestSent }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [selectedSolutions, setSelectedSolutions] = useState<string[]>([]);

    const handleCheckboxChange = (id: string) => {
        setSelectedSolutions((prev: any[]) =>
            prev.includes(id) ? prev.filter((item: any) => item !== id) : [...prev, id]
        );
    };

    const generateSolutionAccessRequest = async () => {
        if (selectedSolutions.length > 0) {
            setIsSaving(true);
            await GenerateSolutionRequestsAPI(selectedSolutions);
            Toaster.success("Solution requests generated successfully!.");
            onSolutionRequestSent();
            setIsSaving(false);
        }
        else
            Toaster.error("Please select at least one solution to generate request.");
    };

    return (
        <>
            {
                solutionAccessData.some(x => x.status !== SolutionUserStatus.Approved && x.status !== SolutionUserStatus.Pending && x.solution.status === SolutionStatus.Publish) ?
                    <>
                        <div className='mb-2 fw-bold'>Available Solutions</div>
                        <div className="wrap-box">
                            {
                                solutionAccessData.filter(x => x.status !== SolutionUserStatus.Pending && x.status !== SolutionUserStatus.Approved && x.solution.status === SolutionStatus.Publish).map((solacc) => (
                                    <div key={`sol_req_${solacc.solution.id}`} className={`checkbox-boxed solution`}>
                                        <Form.Check
                                            type="checkbox"
                                            label={solacc.solution.name}
                                            id={`solution_request_${solacc.solution.id}`}
                                            onChange={() => handleCheckboxChange(solacc.solution.id)}
                                        />
                                        {
                                            solacc.solution.description &&
                                            <InfoTooltip className='me-2' tooltip={solacc.solution.description} />
                                        }
                                    </div>
                                ))
                            }
                        </div>
                    </>
                    :
                    <div className='text-warning'>
                        <i>No other solution is set up to generate a new request.</i>
                    </div>
            }

            {/* Pending reqeusts */}
            {
                solutionAccessData.some(x => x.status === SolutionUserStatus.Pending) &&
                <>
                    <hr className='divider mt-3 mb-2' />
                    <div className='mb-2 fw-bold'>
                        Pending Requests
                        <InfoTooltip className='ms-1' tooltip={"You have already requested for the solutions listed below. Please wait for the admin's approval."} />
                    </div>
                    <div className="wrap-box">
                        {
                            solutionAccessData.filter(x => x.status === SolutionUserStatus.Pending).map((solacc, index) => (
                                <div key={`solacc_${index}`} className='d-flex align-items-center gap-2 bg-muted p-1 px-3 text-black'>
                                    {solacc.solution.name}
                                    {
                                        solacc.solution.description &&
                                        <InfoTooltip tooltip={solacc.solution.description} />
                                    }
                                </div>
                            ))
                        }
                    </div>
                </>
            }

            {/* Solution under enhancements */}
            {
                solutionAccessData.some(x => x.status !== SolutionUserStatus.Approved && x.status !== SolutionUserStatus.Pending && x.solution.status === SolutionStatus.Test) &&
                <>
                    <hr className='divider mt-3 mb-2' />
                    <div className='mb-2 fw-bold'>
                        Solutions Under Enhancements
                        <InfoTooltip className='ms-1' tooltip={"The solutions listed below are currently under enhancement. Please wait for them to be published. You will be able to generate requests once they are available."} />
                    </div>
                    <div className="wrap-box">
                        {
                            solutionAccessData.filter(x => x.status !== SolutionUserStatus.Approved && x.status !== SolutionUserStatus.Pending && x.solution.status === SolutionStatus.Test).map((solacc, index) => (
                                <div key={`solacc_${solacc.solution.id}`} className='d-flex align-items-center gap-2 bg-muted p-1 px-3 text-black'>
                                    {solacc.solution.name}
                                    {
                                        solacc.solution.description &&
                                        <InfoTooltip tooltip={solacc.solution.description} />
                                    }
                                </div>
                            ))
                        }
                    </div>
                </>
            }

            <ModalFooter>
                <Button
                    type="button"
                    variant={'primary'}
                    className="text-capitalize"
                    onClick={generateSolutionAccessRequest}
                    disabled={isSaving || selectedSolutions.length === 0}
                >
                    {
                        isSaving ? (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                        ) : (<></>)
                    }
                    Submit {selectedSolutions.length > 0 ? "(" + selectedSolutions.length + " selected)" : ""}
                </Button>
            </ModalFooter>
        </>
    );
};

export default SolutionRequestGenerator;
